import {
  FlexibleConnectedPositionStrategy,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { ATX_BREAKPOINT_WIDTH } from '@epicuro-next/ataraxia/breakpoint';
import { getOriginPosition, getOverlayPosition } from '@epicuro-next/utilities';
import { filter, fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { PopoverComponent } from '../component/popover.component';
import { PopoverService } from '../service/popover.service';

import { PopoverDirectiveInputs } from './popover-directive-inputs';

/** Popover directive */
@Directive({
  selector: '[atxPopover]',
  exportAs: 'atxPopover',
  standalone: false,
})
export class PopoverDirective
  extends PopoverDirectiveInputs
  implements OnInit, OnDestroy, OnChanges
{
  @Input('atxPopoverRepositionOnHostResize') public updateOnHostResize = false;
  @Output() public atxPopoverOpen = new EventEmitter<void>();
  @Output() public atxPopoverClose = new EventEmitter<{
    referer: ViewContainerRef;
  }>();

  /** PopoverComponent instance */
  private popoverInstance: null | PopoverComponent;
  /** OverlayRef instance */
  private overlayRef: OverlayRef;
  /** ComponentPortal instance */
  private portal: ComponentPortal<PopoverComponent>;
  /** Destroy Subject to be completed on directive destroy */
  private destroy$: Subject<void> = new Subject();
  private reposition$: Subject<void> = new Subject();

  private destroyTriggers$: Subject<void> = new Subject();

  // tslint:disable-next-line:variable-name
  private _opened = false;
  private observer: ResizeObserver;

  public get opened() {
    return this._opened;
  }

  /** Dependencies */
  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private popoverService: PopoverService,
  ) {
    super();
  }

  /**
   * Component OnInit callback
   */
  public ngOnInit(): void {
    this.setupInputs();
    this.setupTriggers();

    this.reposition$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.overlayRef) {
          return;
        }

        this.updateSize();
        this.updatePosition();
      });
  }

  /**
   * Component OnDestroy callback
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.popoverInstance = null;
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      !changes?.triggers?.firstChange &&
      changes?.triggers?.currentValue !== changes?.triggers?.previousValue
    ) {
      this.destroyTriggers$.next();
      this.setupTriggers();
    }
  }

  /**
   * Function to create overlay and dynamically render PopoverComponent inside ComponentPortal
   */
  public open() {
    if (!this.popoverEnabled || this.opened) {
      return;
    }
    this.detach();

    const overlayRef: OverlayRef = this.createOverlayRef();
    this.portal =
      this.portal ||
      new ComponentPortal(PopoverComponent, this.viewContainerRef);

    this.popoverInstance = overlayRef.attach(this.portal).instance;
    this.popoverInstance
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.detach();
      });

    this.popoverInstance
      .afterContentChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        overlayRef.updatePosition();
      });

    this.updateComponentInputs();
    this.popoverInstance.open();
    this.atxPopoverOpen.emit();

    if (this.backdropClass) {
      this.overlayRef.backdropElement?.classList.add(
        ...this.backdropClass.split(' ').filter(Boolean),
      );
    }

    if (this.componentClass) {
      this.popoverInstance.popoverClass += ` ${this.componentClass}`;
    }

    if (this.stickWithFullWidth) {
      this.popoverInstance.popoverClass += ' mw-100';
    }

    if (this.updateOnHostResize) {
      this.observer = new window.ResizeObserver(() => {
        this.reposition$.next();
      });
      this.observer.observe(this.viewContainerRef.element.nativeElement);
    }

    this._opened = true;

    this.subscribeCloseListeners();
  }

  /**
   * Wrapper function to close PopoverComponent
   */
  public close() {
    if (this.popoverInstance) {
      this.popoverInstance.close();

      if (this.observer) {
        this.observer.unobserve(this.viewContainerRef.element.nativeElement);
        this.observer.disconnect();
      }

      this.atxPopoverClose.emit({
        referer: this.viewContainerRef,
      });

      this._opened = false;
    }
  }

  /**
   * Setup Popover open triggers
   */
  private setupTriggers() {
    const destroyNotifier$ = merge(this.destroy$, this.destroyTriggers$);

    if (this.triggers === 'manual') {
      return;
    }

    if (this.triggers === 'hover') {
      if (this.hoverAutoClose) {
        fromEvent(this.elementRef.nativeElement, 'mouseleave')
          .pipe(takeUntil(destroyNotifier$))
          .subscribe(() => {
            this.close();
          });
      }
      fromEvent(this.elementRef.nativeElement, 'mouseenter')
        .pipe(takeUntil(destroyNotifier$))
        .subscribe(() => {
          this.open();
        });
      return;
    }

    fromEvent(this.elementRef.nativeElement, this.triggers)
      .pipe(takeUntil(destroyNotifier$))
      .subscribe(() => {
        this.open();
      });
  }

  /**
   * Function to pass title and content to PopoverComponent
   */
  private updateComponentInputs() {
    if (!this.popoverInstance) {
      return;
    }

    this.popoverInstance.title = this.popoverTitle;
    this.popoverInstance.content = this.atxPopover;
  }

  /**
   * Create OverlayRef if not created yet
   */
  private createOverlayRef(): OverlayRef {
    if (this.overlayRef) {
      this.updateSize();
      return this.overlayRef;
    }

    this.overlayRef = this.popoverService.createOverlay(
      this.elementRef,
      // Magic number "4" is scroll. For some reason angular overlay is not taking this into account when positioning popover
      // Can also be related to custom scroll bar styles that we apply
      this.stickWithFullWidth
        ? {
            xOffset: window.innerWidth <= ATX_BREAKPOINT_WIDTH.MD ? 4 : 0,
            yOffset: -0.5,
          }
        : undefined,
      this.stickWithFullWidth
        ? this.elementRef.nativeElement.getClientRects()[0].width
        : undefined,
      this.closeOnScroll,
      this.hasBackdrop,
    );

    this.overlayRef
      .detachments()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.detach());

    this.updatePosition();

    if (this.panelClass) {
      this.overlayRef.addPanelClass(this.panelClass);
    }

    return this.overlayRef;
  }

  /**
   * Updates the position of the current popover.
   */
  private updatePosition() {
    const position = this.overlayRef.getConfig()
      .positionStrategy as FlexibleConnectedPositionStrategy;
    const origin = getOriginPosition(this.popoverPlacement);
    const overlay = getOverlayPosition(this.popoverPlacement);

    position.withPositions([
      { ...origin.main, ...overlay.main },
      { ...origin.fallback, ...overlay.fallback },
    ]);
  }

  private updateSize() {
    this.overlayRef.updateSize({
      width: this.stickWithFullWidth
        ? this.elementRef.nativeElement.getClientRects()[0].width
        : undefined,
    });
  }

  /**
   * Detach OverlayRef from portal
   */
  private detach() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }

    this.popoverInstance = null;
  }

  /**
   * Subscribe to elements listeners to close PopoverComponent based on autoClose value
   */
  private subscribeCloseListeners() {
    if (this.autoClose === 'outside' || this.autoClose === true) {
      if (this.hasBackdrop) {
        this.overlayRef
          .backdropClick()
          .pipe(
            take(1),
            takeUntil(this.destroy$),
            takeUntil(this.atxPopoverClose),
          )
          .subscribe(() => this.close());
      } else {
        this.overlayRef
          .outsidePointerEvents()
          .pipe(
            filter((event) => {
              // do not close if click is on the element itself, because it leads to close and open immediately
              const triggerEvent =
                event.type === 'click' &&
                this.triggers === 'click' &&
                event.target === this.elementRef.nativeElement;
              return !triggerEvent;
            }),
            take(1),
            debounceTime(100),
            takeUntil(this.destroy$),
            takeUntil(this.atxPopoverClose),
          )
          .subscribe(() => this.close());
      }
    }

    if (this.autoClose === 'inside' || this.autoClose === true) {
      fromEvent(this.overlayRef.overlayElement, 'click')
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          takeUntil(this.atxPopoverClose),
        )
        .subscribe(() => this.close());
    }
  }

  private setupInputs() {
    if (this.triggers === 'hover') {
      this.hasBackdrop = false;
    }
  }
}
